import React, {
    createContext,
    Dispatch,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import {Item} from 'shared/components/Accordion/Item';
import styled from 'styled-components';
import {Header} from 'shared/components/Accordion/Header';
import {Body} from 'shared/components/Accordion/Body';

interface AccordionProps<T = string | number> {
    children?: React.ReactNode;
    defaultActiveKey: T;
    onSelect?: (key: T) => void;
}

interface AccordionContextType<T = string | number> {
    activeKey?: T;
    setActiveKey?: Dispatch<React.SetStateAction<T>>;
}

const AccordionContex = createContext<AccordionContextType>({});

export const useAccordionContext = () => useContext(AccordionContex);

const Accordion = <T extends string | number>({
    children,
    defaultActiveKey,
    onSelect,
}: AccordionProps<T>) => {
    const [key, setKey] = useState(defaultActiveKey);

    useEffect(() => {
        if (key != defaultActiveKey) {
            setKey(defaultActiveKey);
        }
    }, [key, defaultActiveKey]);

    const setActiveKey = useCallback(
        (key: T) => {
            if (onSelect && typeof onSelect === 'function') {
                onSelect(key);
            }

            setKey(key);
        },
        [onSelect]
    );

    return (
        <AccordionContex.Provider value={{activeKey: key, setActiveKey}}>
            <Container>{children}</Container>
        </AccordionContex.Provider>
    );
};

Accordion.Item = Item;
Accordion.Header = Header;
Accordion.Body = Body;

export {Accordion};

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;
